import { useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { HelpCircle, X, ExternalLink } from "lucide-react";
import { MODULES } from "../pages/HelpPage.jsx";
import { resolveHelpKey } from "../config/routeHelpMap.js";

/**
 * Botão flutuante "?" fixo no canto inferior-direito. Ao clicar, abre uma
 * gaveta lateral com o módulo de ajuda correspondente à rota activa.
 * Esconde-se se a rota não tiver mapeamento ou se já estiveres em /ajuda.
 */
export default function ContextualHelpButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const helpKey = useMemo(() => resolveHelpKey(location.pathname), [location.pathname]);
  const mod = useMemo(() => MODULES.find((m) => m.k === helpKey) || null, [helpKey]);

  // Não mostra em /ajuda (já estás no manual completo) nem sem mapeamento
  if (!mod || location.pathname.startsWith("/ajuda") || location.pathname.startsWith("/help")) {
    return null;
  }

  const Icon = mod.icon || HelpCircle;

  return (
    <>
      {/* Botão flutuante */}
      <button
        data-testid="contextual-help-btn"
        type="button"
        onClick={() => setOpen(true)}
        title={`Ajuda — ${mod.title}`}
        aria-label={`Abrir ajuda da página ${mod.title}`}
        className="fixed bottom-5 right-5 z-40 w-11 h-11 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center group"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="absolute right-full mr-3 whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Ajuda desta página
        </span>
      </button>

      {/* Drawer / Sheet lateral */}
      {open && (
        <>
          {/* backdrop */}
          <div
            data-testid="contextual-help-backdrop"
            className="fixed inset-0 bg-black/40 z-50 animate-in fade-in"
            onClick={() => setOpen(false)}
          />
          {/* panel */}
          <aside
            data-testid="contextual-help-panel"
            role="dialog"
            aria-labelledby="contextual-help-title"
            className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white z-50 shadow-2xl flex flex-col animate-in slide-in-from-right"
          >
            {/* header */}
            <header className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 bg-slate-50">
              <div className="flex items-start gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    {mod.group} · Ajuda
                  </p>
                  <h3
                    id="contextual-help-title"
                    data-testid="contextual-help-title"
                    className="text-base font-bold text-slate-900 leading-tight mt-0.5"
                  >
                    {mod.title}
                  </h3>
                </div>
              </div>
              <button
                data-testid="contextual-help-close"
                type="button"
                onClick={() => setOpen(false)}
                className="shrink-0 p-1.5 hover:bg-slate-200 rounded-md text-slate-600"
                aria-label="Fechar ajuda"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            {/* content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              <section>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Para que serve
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {mod.purpose}
                </p>
              </section>

              <section>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Passos
                </p>
                <ol className="space-y-3">
                  {(mod.steps || []).map((s, i) => (
                    <li
                      key={i}
                      data-testid={`contextual-help-step-${i + 1}`}
                      className="flex gap-3"
                    >
                      <span className="shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{s.h}</p>
                        <p className="text-sm text-slate-600 leading-relaxed mt-0.5">{s.t}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>

            {/* footer */}
            <footer className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <Link
                to="/ajuda"
                onClick={() => setOpen(false)}
                data-testid="contextual-help-open-full"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-700"
              >
                Abrir manual completo
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
              >
                Fechar
              </button>
            </footer>
          </aside>
        </>
      )}
    </>
  );
}
